import * as React from 'react';
import { Autocomplete as AutocompletePrimitive } from '@base-ui/react';
import { LoaderCircleIcon, SearchIcon, XIcon } from 'lucide-react';
import useI18n from '../../hooks/useI18n';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { Label } from '../Label';
import {
  floatingFieldLabelClassName,
  floatingFieldLabelInsetClassName,
} from '../Label/helpers';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../InputGroup';
import type { AutocompleteProps } from './types';

/**
 * Resolves the string representation of an option used both for the input value
 * and as the visible label fallback.
 */
function defaultItemToString<Item>(item: Item): string {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (
    typeof item === 'object' &&
    'label' in item &&
    typeof (item as { label: unknown }).label === 'string'
  ) {
    return (item as { label: string }).label;
  }
  return String(item);
}

/**
 * A free-text input with a filtered suggestions dropdown, built on the Base UI
 * Autocomplete primitive. The value is the typed string; options are
 * suggestions that fill the input (and fire `onItemSelect`) when chosen.
 *
 * For async search, pass already-filtered `items`, set `manualFiltering`, and
 * toggle `loading`.
 *
 * Demo:
 * - [Autocomplete](https://base-ui.com/react/components/autocomplete)
 */
function Autocomplete<Item>({
  label,
  items,
  value,
  defaultValue,
  onValueChange,
  onItemSelect,
  itemToStringValue,
  itemToStringLabel,
  renderItem,
  manualFiltering = false,
  loading = false,
  loadingText,
  noItemsText,
  placeholder,
  showClear = true,
  showSearchIcon = false,
  openOnInputClick = true,
  disabled = false,
  required = false,
  error = false,
  name,
  id,
  className,
  inputProps,
  popupContainer,
  side = 'bottom',
  align = 'start',
}: AutocompleteProps<Item>) {
  const { t } = useI18n();
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const labelId = label != null ? `${inputId}-label` : undefined;

  const toValue = itemToStringValue ?? defaultItemToString;
  const toLabel = itemToStringLabel ?? toValue;

  return (
    <AutocompletePrimitive.Root<Item>
      items={items}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(nextValue) => onValueChange?.(nextValue)}
      itemToStringValue={toValue}
      // When the caller filters externally (async search), disable the built-in
      // filtering so we never hide server-provided results.
      filter={manualFiltering ? null : undefined}
      disabled={disabled}
      required={required}
      name={name}
      openOnInputClick={openOnInputClick}
      autoHighlight
    >
      <div
        data-slot='autocomplete'
        className={cn(
          'eye:group/autocomplete-field eye:relative eye:block eye:w-full',
          className,
        )}
      >
        {label != null && (
          <Label
            id={labelId}
            htmlFor={inputId}
            data-slot='autocomplete-label'
            className={cn(
              floatingFieldLabelClassName,
              floatingFieldLabelInsetClassName,
              'eye:top-1',
              // Disabled state
              'eye:group-has-disabled/autocomplete-field:text-grey-disabled',
              // Validation state
              'eye:group-has-[[data-slot=input-group-control][aria-invalid=true]]/autocomplete-field:text-red-100',
            )}
          >
            {required ? `${label} *` : label}
          </Label>
        )}
        <AutocompletePrimitive.InputGroup
          render={<InputGroup />}
          data-slot='autocomplete-input-group'
        >
          {showSearchIcon && (
            <InputGroupAddon
              align='inline-start'
              className='eye:pointer-events-none eye:cursor-default'
            >
              <SearchIcon
                data-slot='autocomplete-input-icon'
                className='eye:size-16 eye:text-grey-100'
              />
            </InputGroupAddon>
          )}
          <AutocompletePrimitive.Input
            render={<InputGroupInput />}
            id={inputId}
            aria-labelledby={labelId}
            aria-invalid={error || undefined}
            placeholder={placeholder}
            {...inputProps}
          />
          <InputGroupAddon
            align='inline-end'
            className='eye:gap-4 eye:pr-8'
          >
            {loading && (
              <LoaderCircleIcon
                data-slot='autocomplete-loading-icon'
                aria-hidden='true'
                className='eye:size-16 eye:shrink-0 eye:animate-spin eye:text-grey-100'
              />
            )}
            {showClear && (
              <AutocompletePrimitive.Clear
                data-slot='autocomplete-clear'
                aria-label={t('clearValue')}
                render={<Button variant='ghost' size='icon-xs' />}
                className='eye:pointer-events-auto'
              >
                <XIcon className='eye:pointer-events-none eye:size-16' />
              </AutocompletePrimitive.Clear>
            )}
          </InputGroupAddon>
        </AutocompletePrimitive.InputGroup>
      </div>
      <AutocompletePrimitive.Portal container={popupContainer}>
        <AutocompletePrimitive.Positioner
          data-slot='autocomplete-positioner'
          side={side}
          sideOffset={6}
          align={align}
          className='eye:isolate eye:z-1400'
        >
          <AutocompletePrimitive.Popup
            data-slot='autocomplete-content'
            className={cn(
              // Layout and sizing
              'eye:group/autocomplete-content eye:relative eye:z-1410 eye:box-border',
              'eye:w-(--anchor-width) eye:overflow-hidden',
              // Surface
              'eye:rounded-md eye:border eye:border-grey-40 eye:bg-white eye:text-black',
              'eye:shadow-popover-content',
              // Motion
              'eye:duration-100 eye:data-closed:animate-out eye:data-closed:fade-out-0 eye:data-closed:zoom-out-95',
              'eye:data-open:animate-in eye:data-open:fade-in-0 eye:data-open:zoom-in-95',
              'eye:data-[side=bottom]:slide-in-from-top-2 eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2 eye:data-[side=top]:slide-in-from-bottom-2',
            )}
          >
            {loading && (
              <div
                role='status'
                data-slot='autocomplete-loading'
                className='eye:body-m eye:flex eye:min-h-[44px] eye:items-center eye:justify-center eye:gap-8 eye:text-grey-disabled'
              >
                <LoaderCircleIcon
                  aria-hidden='true'
                  className='eye:size-16 eye:shrink-0 eye:animate-spin'
                />
                {loadingText ?? t('loading')}
              </div>
            )}
            {!loading && (
              <AutocompletePrimitive.Empty
                data-slot='autocomplete-empty'
                className='eye:body-m eye:hidden eye:min-h-[44px] eye:w-full eye:items-center eye:justify-center eye:text-grey-disabled eye:group-data-empty/autocomplete-content:flex'
              >
                {noItemsText ?? t('noResultsFound')}
              </AutocompletePrimitive.Empty>
            )}
            <AutocompletePrimitive.List
              data-slot='autocomplete-list'
              className='eye:max-h-[324px] eye:scroll-py-4 eye:overflow-y-auto eye:py-4xs eye:data-empty:p-0'
            >
              <AutocompletePrimitive.Collection>
                {(item: Item, index: number) => (
                  <AutocompletePrimitive.Item
                    key={`${toValue(item)}-${index}`}
                    value={item}
                    onClick={() => onItemSelect?.(item)}
                    data-slot='autocomplete-item'
                    className={cn(
                      // Layout
                      'eye:flex eye:min-h-[44px] eye:w-full eye:items-center eye:gap-8 eye:border-0 eye:px-16 eye:py-[6px]',
                      // Typography and transitions
                      'eye:text-14 eye:text-black eye:transition-[background-color,color] eye:duration-150',
                      // Interaction
                      'eye:cursor-pointer eye:outline-none eye:select-none',
                      'eye:hover:bg-grey-10 eye:hover:text-black eye:focus:outline-none eye:focus-visible:outline-none',
                      // Stateful variants
                      'eye:data-disabled:pointer-events-none eye:data-disabled:opacity-50',
                      'eye:data-highlighted:bg-grey-10 eye:data-highlighted:text-black',
                      // Shape, not colour alone: the tinted background is far below 3:1.
                      'eye:data-highlighted:inset-ring-2 eye:data-highlighted:inset-ring-black',
                    )}
                  >
                    {renderItem?.(item) ?? toLabel(item)}
                  </AutocompletePrimitive.Item>
                )}
              </AutocompletePrimitive.Collection>
            </AutocompletePrimitive.List>
          </AutocompletePrimitive.Popup>
        </AutocompletePrimitive.Positioner>
      </AutocompletePrimitive.Portal>
    </AutocompletePrimitive.Root>
  );
}

Autocomplete.displayName = 'Autocomplete';

export { Autocomplete };
